import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  User as UserIcon,
  LogOut,
  Building2
} from "lucide-react";
import { pageHeaders } from "../config/pageHeaders";
import { useOrganizationLogo } from "../hooks/useOrganizationLogo";
import { useOrganizationData } from "../hooks/useOrganizationData";
import { logout } from "../services/authService";
import { STORAGE_KEYS } from "../constants/storage";

export default function TopBar() {
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Use custom hooks for logo and organization data
  const { logoUrl, fetchLogo } = useOrganizationLogo(true); // Enable cache-busting
  const { data: orgData } = useOrganizationData(["company_name"]);
  const companyName = orgData?.company_name || "";

  useEffect(() => {
    const userData = localStorage.getItem(STORAGE_KEYS.USER);
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchLogo();
  }, [fetchLogo]);

  const handleLogout = () => {
    // Use centralized logout function that clears all auth data
    logout();
    navigate("/login");
  };

  // Dynamic header content based on current route
  const getHeaderContent = () => {
    const path = location.pathname;

    // Integration detail page - use company name
    if (path.startsWith("/integration/")) {
      return {
        ...pageHeaders.dashboard,
        title: companyName || pageHeaders.dashboard.title
      };
    }

    // Dashboard page - use company name
    if (path === "/dashboard") {
      return {
        ...pageHeaders.dashboard,
        title: companyName || pageHeaders.dashboard.title
      };
    }

    if (path === "/ask-orbit") {
      return pageHeaders.askOrbit;
    }

    if (path.startsWith("/workflows/notifications")) {
      return pageHeaders.workflowNotifications;
    }

    if (path === "/profile-settings") {
      return pageHeaders.profileSettings;
    }

    if (path === "/company-settings") {
      return pageHeaders.companySettings;
    }

    // Workflows page - use company name
    if (path === "/workflows") {
      return {
        ...pageHeaders.workflows,
        title: companyName || pageHeaders.workflows.title
      };
    }

    // Settings page
    if (path === "/settings") {
      return pageHeaders.settings;
    }

    // Activity page
    if (path === "/activity") {
      return pageHeaders.activity;
    }

    // Documentation page
    if (path === "/documentation") {
      return pageHeaders.documentation;
    }

    // Default fallback
    return {
      ...pageHeaders.dashboard,
      title: companyName || pageHeaders.dashboard.title
    };
  };

  const headerContent = getHeaderContent();

  return (
    <div className="bg-white border-b border-gray-200 px-8 py-2 flex justify-between">
      {/* Header Section */}
      <div className="flex items-center gap-4">
        {/* Company Logo */}
        <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center overflow-hidden">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company Logo"
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <Building2 className="w-5 h-5 text-gray-400" />
          )}
        </div>

        {/* Header Text */}
        <div>
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">
              {headerContent.title}
            </h1>
          </div>
          <p className="text-gray-600 text-sm">{headerContent.subtitle}</p>
        </div>
      </div>
      <div className="flex items-center justify-end space-x-4">
        {/* {headerContent.showRequestButton && (
          <button className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Sparkles className="w-4 h-4" />
            <span className="font-medium text-sm">Request Integration</span>
          </button>
        )} */}
        {/* Settings Icon */}
        {/* <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <Settings className="w-5 h-5" />
        </button> */}

        {/* User Icon with Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <UserIcon className="w-5 h-5" />
          </button>

          {/* User Dropdown Menu */}
          {showUserMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-2 border-b border-gray-200">
                <p className="text-sm font-medium text-gray-900">
                  {user?.name || "User"}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email || "user@example.com"}
                </p>
              </div>
              {/* <button
                onClick={() => {
                  setShowUserMenu(false);
                  // Navigate to profile
                }}
                className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <UserIcon className="w-4 h-4" />
                <span>Your Profile</span>
              </button>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  navigate("/settings");
                }}
                className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </button> */}
              <div className="mt-1 pt-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
